
import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInstallState } from '@/hooks/useInstallState';
import { closeSKOverlay, showSKOverlay } from '@/utils/nativeInterface';
import AppDetailHeader from '@/components/AppDetailHeader';
import AppDescription from '@/components/AppDescription';
import { useAppStoreDetails } from '@/hooks/useAppStoreDetails';
import LoadingAppDetail from '@/components/LoadingAppDetail';
import ErrorAppDetail from '@/components/ErrorAppDetail';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { App } from '@/types/App';

const AppDetail: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  
  // Fetch app details from the App Store
  const { appDetails, isLoading, error } = useAppStoreDetails(appId || '');
  
  console.log('=== AppDetail component mounted ===');
  console.log('AppDetail - App ID from params:', appId);
  console.log('AppDetail - Loading state:', isLoading);
  console.log('AppDetail - Error state:', error);
  console.log('AppDetail - App data:', appDetails?.trackName);
  
  const { installPressed, handleInstallPress, resetInstallState } = useInstallState();
  
  // Convert appDetails to App type
  const app: App | null = useMemo(() => {
    if (!appDetails) return null;
    
      return {
        id: String(appDetails.trackId),
        name: appDetails.trackName,
        icon: appDetails.artworkUrl100,
        description: appDetails.description || '',
        version: appDetails.version || '',
        appStoreId: String(appDetails.trackId),
        category: appDetails.primaryGenreName,
        bundleId: appDetails.bundleId
      };
  }, [appDetails]);
  
  // Debug install button state
  if (app) {
    console.log('Install button debug for app:', app.name);
    console.log('- App Store ID:', app.appStoreId);
    console.log('- App Store ID valid:', app.appStoreId && app.appStoreId.trim() !== '');
    console.log('- Install pressed:', installPressed);
    console.log('- Button should be disabled:', (!app.appStoreId || app.appStoreId.trim() === '') || installPressed);
  }

  // Memoize handleInstallPress to prevent unnecessary re-renders
  const memoizedHandleInstallPress = useCallback(() => {
    handleInstallPress();
    
    // Show SKOverlay with dismiss callback to reset install state
    if (app?.appStoreId) {
      showSKOverlay(app.appStoreId, resetInstallState);
    }
  }, [handleInstallPress, resetInstallState, app?.appStoreId]);

  // Cleanup SKOverlay when component unmounts
  useEffect(() => {
    return () => {
      closeSKOverlay();
    };
  }, []);

  const handleBack = useCallback(() => {
    navigate(`/${serial}`);
  }, [navigate, serial]);

  // Show loading state
  if (isLoading) {
    return <LoadingAppDetail />;
  }

  // Show error state
  if (error || !app) {
    return <ErrorAppDetail error={error ? new Error(error) : new Error('App not found')} />;
  }

  return (
    <div className="h-screen bg-background relative overflow-hidden">
      {/* Main content */}
      <div className="relative z-10 h-full bg-background flex flex-col">
        {/* Fixed Header that never scrolls */}
        <div className="fixed top-0 left-0 right-0 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] z-20 flex-shrink-0">
          <AppDetailHeader 
            app={app} 
            installPressed={installPressed}
            onInstallPress={memoizedHandleInstallPress}
            onBack={handleBack}
          />
        </div>
        
        {/* Scrollable content with top padding to account for fixed header */}
        <div className="pt-44 flex-1 min-h-0 overflow-auto">
          <AppDescription app={app} />
        </div>
      </div>
    </div>
  );
};

export default AppDetail;
